import styles from 'legacy/common/components/form/Form.module.scss';

import React, { useState } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import classnames from 'classnames';
import { Form, Formik } from 'formik';

import { isEmailAddress } from 'legacy/auth/helpers';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import PhoneInput from 'legacy/common/components/phone-input/PhoneInput';
import { getCallingCode, getCountryCode } from 'legacy/common/utils/country';

const getLoginSchema = (t: any, showPhoneOption: boolean) =>
  Yup.object().shape({
    email: Yup.string().when([], {
      is: () => !showPhoneOption,
      then: (schema) =>
        schema
          .required(t('loginForm.validation.required'))
          .test('is-valid-email', t('loginForm.validation.invalidEmail'), (value) => {
            if (!value) return true;
            return isEmailAddress(value);
          }),
      otherwise: (schema) => schema.notRequired()
    }),
    phoneNumber: Yup.string().when([], {
      is: () => showPhoneOption,
      then: (schema) =>
        schema
          .required(t('loginForm.validation.required'))
          .test('is-valid-phone', t('loginForm.validation.minDigits'), (value) => {
            if (!value) return true;
            return value.length >= 5;
          }),
      otherwise: (schema) => schema.notRequired()
    })
  });

type LoginFormProps = {
  isLoading: boolean;
  errorMessage: string;
  showMoreOptions: boolean;
  onShowMethodsList: () => void;
  onSubmit: (values: {
    email?: string;
    phoneNumber?: string;
    countryCode: string;
    callingCode: string;
  }) => void;
};

const LoginForm: React.FC<LoginFormProps> = ({
  errorMessage,
  isLoading,
  showMoreOptions,
  onShowMethodsList,
  onSubmit
}) => {
  const { t } = useTranslation('auth');
  const currentCountryCode = getCountryCode();
  const currentCallingCode = getCallingCode(currentCountryCode);

  return (
    <>
      <p className={styles.submitError}>{errorMessage}</p>

      <Formik
        initialValues={{
          email: '',
          phoneNumber: '',
          countryCode: currentCountryCode,
          callingCode: currentCallingCode
        }}
        onSubmit={onSubmit}
        validationSchema={getLoginSchema(t, showMoreOptions)}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          errors,
          touched,
          dirty,
          values,
          setFieldValue,
          handleChange,
          handleBlur
        }) => (
          <Form>
            {!showMoreOptions ? (
              <div className={styles.group}>
                <p className={classnames(styles.label, styles.small)}>
                  {t('loginForm.emailLabel')}
                </p>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    data-testid="input-email"
                    placeholder={t('loginForm.emailPlaceholder')}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                    className={classnames(styles.input, {
                      [styles.errorField]: errors.email && touched.email
                    })}
                    autoFocus
                  />

                  {errors.email && touched.email && (
                    <div className={classnames(styles.error, styles.right)}>
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.group}>
                <p className={classnames(styles.label, styles.small)}>
                  {t('loginForm.phoneNumberLabel')}
                </p>
                <div className="relative">
                  <PhoneInput
                    testId="input-phone-number"
                    value={values.phoneNumber}
                    hasError={!!(errors.phoneNumber && touched.phoneNumber)}
                    countryCode={values.countryCode}
                    onBlur={handleBlur('phoneNumber')}
                    onChange={handleChange('phoneNumber')}
                    onCountryChange={(selected) => {
                      setFieldValue('countryCode', selected.countryCode);
                      setFieldValue('callingCode', selected.value);
                    }}
                    autoFocus
                  />

                  {errors.phoneNumber && touched.phoneNumber && (
                    <div className={classnames(styles.error, styles.right)}>
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={styles.group}>
              <Button
                isSubmit
                disabled={!dirty}
                loading={isLoading}
                testId="button-submit"
                type={ButtonType.Primary}
              >
                {t('loginForm.continueButton')}
              </Button>
            </div>

            <a
              className="text-primary-500 block mb-4 cursor-pointer"
              onClick={onShowMethodsList}
              data-testid="button-show-methods"
            >
              {t('loginForm.moreOptions')}
            </a>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default LoginForm;
